<script lang="ts">
  import { onMount } from "svelte";
  import { loadWalletDataset } from "../wallet/data";
  import type { WalletCard, WalletDataset } from "../wallet/types";
  import {
    loadProfile,
    saveProfile,
    loadSelectedIds,
    saveSelectedIds,
    clearProfile,
    loadCustomCards,
    saveCustomCards,
    clearCustomCards,
    isWelcomed,
    markWelcomed,
    resetWelcomed,
    type Profile,
    type CustomCard
  } from "./profile";
  import Welcome from "./Welcome.svelte";
  import Onboarding from "./Onboarding.svelte";
  import Home from "./Home.svelte";
  import HomeSkeleton from "./HomeSkeleton.svelte";
  import Button from "./ui/Button.svelte";
  import Toaster from "./Toaster.svelte";

  let dataset: WalletDataset | null = null;
  let loadError: string | null = null;
  let profile: Profile | null = null;
  let selectedIds: string[] = [];
  let customCards: CustomCard[] = [];
  let view: "welcome" | "onboarding" | "home" = "welcome";
  let themeAttr: "light" | "dark" = "dark";

  onMount(async () => {
    const t = document.documentElement.dataset.appTheme;
    themeAttr = t === "light" ? "light" : "dark";

    try {
      profile = loadProfile();
      selectedIds = loadSelectedIds();
      customCards = loadCustomCards();
      // Decide initial view
      const welcomed = isWelcomed();
      const hasProfile = !!profile && selectedIds.length > 0;
      if (!welcomed) {
        view = "welcome";
      } else if (hasProfile) {
        view = "home";
      } else {
        view = "onboarding";
      }
      dataset = await loadWalletDataset();
    } catch (err) {
      loadError = err instanceof Error ? err.message : String(err);
    }
  });

  function customToWalletCard(c: CustomCard): WalletCard {
    return {
      aliases: [],
      annualFeeText: c.annualFeeText,
      autoDiscoveredAt: "",
      coverageStatus: "user_self_reported",
      id: c.id,
      imageAlt: c.name,
      imageUrl: null,
      issueMarket: "USER_SELF_REPORTED",
      issuer: c.issuer,
      issuerCode: "",
      level: c.level,
      name: c.name,
      networks: [],
      shortName: c.name,
      sourceLevel: "user_self_reported",
      sourceUrls: []
    };
  }

  function handleStart() {
    markWelcomed();
    view = "onboarding";
  }
  function handleContinue() {
    markWelcomed();
    view = "home";
  }
  function handleComplete(p: Profile, ids: string[], newCustom: CustomCard[]) {
    profile = p;
    selectedIds = ids;
    customCards = newCustom;
    saveProfile(p);
    saveSelectedIds(ids);
    saveCustomCards(newCustom);
    markWelcomed();
    view = "home";
  }
  function handleEditProfile() {
    view = "onboarding";
  }
  function handleReset() {
    clearProfile();
    clearCustomCards();
    resetWelcomed();
    profile = null;
    customCards = [];
    selectedIds = [];
    saveSelectedIds([]);
    view = "welcome";
  }
  function toggleTheme() {
    themeAttr = themeAttr === "dark" ? "light" : "dark";
    try {
      document.documentElement.dataset.appTheme = themeAttr;
      localStorage.setItem("mofaxiaoka.app.theme", themeAttr);
    } catch {}
  }
  function handleProfileChange(p: Profile) {
    profile = p;
    saveProfile(p);
  }
  function handleCardsChange(next: { selectedIds: string[]; customCards: CustomCard[] }) {
    selectedIds = next.selectedIds;
    customCards = next.customCards;
    saveSelectedIds(next.selectedIds);
    saveCustomCards(next.customCards);
    if (profile?.pinnedDefaultCardId && !next.selectedIds.includes(profile.pinnedDefaultCardId)) {
      const cleared: Profile = { ...profile, pinnedDefaultCardId: undefined };
      profile = cleared;
      saveProfile(cleared);
    }
  }
  function handleGoToWelcome() {
    view = "welcome";
  }

  $: combinedCardCatalog = dataset
    ? [...dataset.cards, ...customCards.map(customToWalletCard)]
    : [];
  $: selectedCards = combinedCardCatalog.filter(c => selectedIds.includes(c.id));
  $: hasCompletedOnboarding = !!profile && selectedIds.length > 0;
</script>

<div class="mvp-root" data-theme={themeAttr}>
  <Toaster />
  {#if loadError}
    <div class="mx-auto max-w-2xl px-5 py-10">
      <div class="rounded-lg border border-destructive/40 bg-destructive/10 p-6 text-sm">
        <p class="mb-2 font-semibold text-destructive">数据加载失败</p>
        <p class="text-destructive/80">{loadError}</p>
        <p class="mt-3 text-xs text-muted-foreground">
          检查网络后刷新；如果一直失败可能是 data/*.json 缺失。
        </p>
      </div>
    </div>
  {:else if view === "welcome"}
    <Welcome
      theme={themeAttr}
      {hasCompletedOnboarding}
      onStart={handleStart}
      onContinue={handleContinue}
      onToggleTheme={toggleTheme}
    />
  {:else if !dataset}
    <HomeSkeleton />
  {:else if view === "onboarding"}
    <Onboarding
      cards={dataset.cards}
      {customCards}
      initialSelected={selectedIds}
      onComplete={handleComplete}
    />
    {#if profile}
      <div class="mx-auto flex max-w-2xl flex-wrap justify-center gap-3 px-5 pb-6">
        <Button variant="ghost" size="sm" on:click={() => (view = "home")}>取消修改</Button>
        <Button variant="ghost" size="sm" on:click={handleGoToWelcome}>回到主页</Button>
        <Button variant="ghost" size="sm" on:click={handleReset}>重置画像</Button>
      </div>
    {/if}
  {:else if view === "home" && profile}
    <Home
      {profile}
      {selectedCards}
      allBenefits={dataset.benefits}
      allCatalog={combinedCardCatalog}
      {customCards}
      theme={themeAttr}
      onEditProfile={handleEditProfile}
      onProfileChange={handleProfileChange}
      onCardsChange={handleCardsChange}
      onToggleTheme={toggleTheme}
      onGoHome={handleGoToWelcome}
    />
  {/if}
</div>
